import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { confirmSuccessBody, confirmSuccessTitle } from '../shared/authConfirm';

const AuthConfirm = ({ request, onContinue }) => {
  const { colors } = useTheme();
  const [status, setStatus] = useState('working');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const finish = async () => {
      if (request.error && !request.tokenHash && !request.code && !request.accessToken) {
        if (!cancelled) {
          setStatus('error');
          setMessage(request.error);
        }
        return;
      }
      try {
        if (request.tokenHash) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: request.tokenHash,
            type: request.type,
          });
          if (error) throw error;
        } else if (request.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(request.code);
          if (error) {
            const { data } = await supabase.auth.getSession();
            if (!data.session) throw error;
          }
        } else {
          const { data, error } = await supabase.auth.getSession();
          if (error) throw error;
          if (!data.session) {
            throw new Error('This link is invalid or has expired. Request a new email from the sign-in page.');
          }
        }
        if (!cancelled) {
          setStatus('ready');
          setMessage('');
          if (window.history && window.location.pathname === '/auth/confirm') {
            window.history.replaceState({}, '', '/auth/confirm');
          }
        }
      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setMessage(error.message || 'This link is invalid or has expired.');
        }
      }
    };
    finish();
    return () => {
      cancelled = true;
    };
  }, [request]);

  const savePassword = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    setSaving(true);
    setMessage('');
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) {
      setMessage(error.message || 'Could not save the password.');
      return;
    }
    setStatus('saved');
  };

  const title = status === 'error'
    ? 'This link did not work'
    : status === 'saved'
      ? 'Password saved'
      : status === 'working'
        ? 'Checking your email'
        : confirmSuccessTitle(request.type);
  const body = status === 'error'
    ? (message || 'This link is invalid or has expired. Request a new email from the sign-in page.')
    : status === 'saved'
      ? 'Your password is updated. Continue to sign in.'
      : status === 'working'
        ? 'One moment.'
        : confirmSuccessBody(request.type);

  const card = {
    maxWidth: '440px',
    width: '100%',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '2rem 1.5rem',
    textAlign: 'center',
  };
  const button = {
    display: 'inline-block',
    marginTop: '1.25rem',
    padding: '0.85rem 1.4rem',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '44px',
  };
  const input = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem',
    marginTop: '0.75rem',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.text,
    fontSize: '1rem',
  };

  return (
    <div className="app-scroll app-scroll-center" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', background: colors.background }}>
      <div style={card}>
        <div style={{ fontSize: '1.6rem', letterSpacing: '0.08em', color: '#59c4c4', fontWeight: 500 }}>PACARION</div>
        <h1 style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 600 }}>{title}</h1>
        <p style={{ color: colors.textSecondary, lineHeight: 1.5 }}>{body}</p>
        {status === 'ready' && message && <p style={{ color: '#b91c1c' }}>{message}</p>}
        {status === 'ready' && request.type === 'recovery' ? (
          <form onSubmit={savePassword}>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="New password" aria-label="New password" style={input} />
            <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="New password again" aria-label="New password again" style={input} />
            <button type="submit" style={button} disabled={saving}>{saving ? 'Saving…' : 'Save password'}</button>
          </form>
        ) : status !== 'working' && (
          <button type="button" style={button} onClick={onContinue}>
            {status === 'error' ? 'Continue to sign in' : 'Continue'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthConfirm;
